import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JjamesButtonComponent, JJamesCardComponent } from 'shared-lib';
import {
    MasterService,
    ParkingSpaceService,
    ParkingZoneService,
    InfrastructureService,
    MasterDeviceAssignmentService,
    CreateMasterControllerRequest,
    UpdateMasterControllerRequest,
    CreateMasterDeviceAssignmentRequest,
    MasterControllerDto,
    DeviceAddressDto,
    InfrastructureDto,
    DeviceGroupDto,
    MasterAssignmentType
} from '../../../../../api/gen';
import { firstValueFrom } from 'rxjs';
import { ParkingSpaceDto } from '../../../../../api/gen/model/parkingSpaceDto';
import { ParkingZoneDto } from '../../../../../api/gen/model/parkingZoneDto';

@Component({
    selector: 'app-master-controller-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        JjamesButtonComponent,
        JJamesCardComponent
    ],
    templateUrl: './master-controller-form.component.html',
    styleUrl: './master-controller-form.component.scss'
})
export class MasterControllerFormComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private masterService = inject(MasterService);
    private parkingSpaceService = inject(ParkingSpaceService);
    private parkingZoneService = inject(ParkingZoneService);
    private infrastructureService = inject(InfrastructureService);
    private masterDeviceAssignmentService = inject(MasterDeviceAssignmentService);
    private snackBar = inject(MatSnackBar);

    parkingSpaceId: string | null = null;
    masterId: string | null = null;
    isLoading = signal(false);
    isSaving = signal(false);
    isEditMode = signal(false);

    // Data
    parkingSpace = signal<ParkingSpaceDto | null>(null);
    parkingZones = signal<ParkingZoneDto[]>([]);
    infrastructures = signal<InfrastructureDto[]>([]);
    deviceGroups = signal<DeviceGroupDto[]>([]);

    // Assignment types
    assignmentTypes = ['InfrastructureWide', 'GroupSpecific'];
    selectedInfrastructureIds = signal<string[]>([]);
    selectedDeviceGroupIds = signal<string[]>([]);

    // Infrastructure id → owning zone / parking space name, for labelling the assignment lists
    private infrastructureNames = computed(() => {
        const names = new Map<string, string>();
        const parkingSpace = this.parkingSpace();
        if (parkingSpace?.parkingSpaceInfrastructureId) {
            names.set(parkingSpace.parkingSpaceInfrastructureId, parkingSpace.name || 'Parking Space');
        }
        this.parkingZones().forEach(zone => {
            if (zone.zoneInfrastructureId) {
                names.set(zone.zoneInfrastructureId, zone.name);
            }
        });
        return names;
    });

    // Device group id → name of the zone / parking space it belongs to
    private deviceGroupOwnerNames = computed(() => {
        const names = new Map<string, string>();
        this.infrastructures().forEach(infra => {
            const owner = this.infrastructureLabel(infra);
            infra.deviceGroups?.forEach(group => {
                if (group.id) names.set(group.id, owner);
            });
        });
        return names;
    });

    masterControllerForm: FormGroup;

    constructor() {
        this.masterControllerForm = this.fb.group({
            deviceName: ['', Validators.required],
            deviceDescription: [''],
            deviceHardwareID: ['', Validators.required],
            iotHubDeviceId: ['', [Validators.required], [this.validateUniquIoTHubDeviceId.bind(this)]],
            iotHubHostName: [''],
            authenticationMethod: [''],
            firmwareVersion: [''],
            operatingSystem: [''],
            cpuModel: [''],
            ramMb: ['', [Validators.pattern(/^\d+$/)]],
            storageGb: ['', [Validators.pattern(/^\d+$/)]],
            macAddress: [''],
            ipAddress: [''],
            hostname: [''],
            // Address fields
            addressLine1: [''],
            addressLine2: [''],
            city: [''],
            postalCode: [''],
            region: [''],
            countryCode: [''],
            // Assignment fields
            assignmentType: ['InfrastructureWide', Validators.required]
        });
    }

    ngOnInit(): void {
        this.parkingSpaceId = this.route.snapshot.paramMap.get('parkingSpaceId');
        this.masterId = this.route.snapshot.paramMap.get('masterId');

        if (this.masterId) {
            this.isEditMode.set(true);
            this.loadMasterController();
        } else {
            // Auto-generate hardware ID for new controller
            this.generateHardwareID();
            // Load parking space and related data for assignment
            this.loadParkingSpaceAndZones();
        }
    }

    private loadParkingSpaceAndZones(): void {
        if (!this.parkingSpaceId) return;

        this.isLoading.set(true);

        this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(this.parkingSpaceId).subscribe({
            next: (parkingSpace) => {
                this.parkingSpace.set(parkingSpace);
                this.parkingZones.set(parkingSpace.parkingZones || []);
                this.loadInfrastructuresAndDeviceGroups();
            },
            error: (error) => {
                console.error('Error loading parking space:', error);
                this.isLoading.set(false);
            }
        });
    }

    private loadInfrastructuresAndDeviceGroups(): void {
        const parkingSpace = this.parkingSpace();
        const zones = this.parkingZones();

        // Collect all infrastructure IDs to fetch
        const infrastructureIds: Set<string> = new Set();

        // Add parking space infrastructure ID
        if (parkingSpace?.parkingSpaceInfrastructureId) {
            infrastructureIds.add(parkingSpace.parkingSpaceInfrastructureId);
        }

        // Add zone infrastructure IDs
        zones.forEach(zone => {
            if (zone.zoneInfrastructureId) {
                infrastructureIds.add(zone.zoneInfrastructureId);
            }
        });

        // If no infrastructures found, just set empty arrays
        if (infrastructureIds.size === 0) {
            this.infrastructures.set([]);
            this.deviceGroups.set([]);
            this.isLoading.set(false);
            return;
        }

        // Fetch all infrastructure details in one request
        firstValueFrom(this.infrastructureService.infrastructuresByIdsPost({
            infrastructureIds: Array.from(infrastructureIds)
        }))
            .then((infrastructures) => {
                const allInfrastructures: InfrastructureDto[] = infrastructures ?? [];
                const allDeviceGroups: DeviceGroupDto[] = [];

                // Collect all device groups from all infrastructures
                allInfrastructures.forEach(infrastructure => {
                    if (infrastructure.deviceGroups && Array.isArray(infrastructure.deviceGroups)) {
                        allDeviceGroups.push(...infrastructure.deviceGroups);
                    }
                });

                this.infrastructures.set(allInfrastructures);
                this.deviceGroups.set(allDeviceGroups);
                this.isLoading.set(false);
            })
            .catch((error) => {
                console.error('Error loading infrastructures and device groups:', error);
                this.infrastructures.set([]);
                this.deviceGroups.set([]);
                this.isLoading.set(false);
            });
    }

    infrastructureLabel(infra: InfrastructureDto): string {
        return (infra.id && this.infrastructureNames().get(infra.id)) || infra.infrastructureType || 'Unknown';
    }

    deviceGroupOwner(group: DeviceGroupDto): string | undefined {
        return group.id ? this.deviceGroupOwnerNames().get(group.id) : undefined;
    }

    private loadMasterController(): void {
        if (!this.masterId) return;

        this.isLoading.set(true);
        this.masterService.masterControllersIdGet(this.masterId).subscribe({
            next: (result) => {
                if (result) {
                    this.populateForm(result);
                }
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error loading master controller:', error);
                this.snackBar.open('Failed to load master controller', 'Close', { duration: 3000 });
                this.isLoading.set(false);
            }
        });
    }

    private populateForm(masterController: MasterControllerDto): void {
        this.masterControllerForm.patchValue({
            deviceName: masterController.deviceName,
            deviceDescription: masterController.deviceDescription,
            deviceHardwareID: masterController.deviceHardwareID,
            iotHubDeviceId: masterController.iotHubDeviceId,
            iotHubHostName: masterController.iotHubHostName,
            authenticationMethod: masterController.authenticationMethod,
            firmwareVersion: masterController.firmwareVersion,
            operatingSystem: masterController.operatingSystem,
            cpuModel: masterController.cpuModel,
            ramMb: masterController.ramMb,
            storageGb: masterController.storageGb,
            macAddress: masterController.macAddress,
            ipAddress: masterController.ipAddress,
            hostname: masterController.hostname
        });

        if (masterController.deviceAddress) {
            this.masterControllerForm.patchValue({
                addressLine1: masterController.deviceAddress.firstAddressLine,
                addressLine2: masterController.deviceAddress.secondAddressLine,
                city: masterController.deviceAddress.locality,
                postalCode: masterController.deviceAddress.postCode,
                region: masterController.deviceAddress.region,
                countryCode: masterController.deviceAddress.countryCode
            });
        }
    }

    async onSubmit(): Promise<void> {
        if (this.masterControllerForm.invalid) {
            this.masterControllerForm.markAllAsTouched();
            return;
        }

        this.isSaving.set(true);
        const formValue = this.masterControllerForm.value;

        try {
            if (this.isEditMode()) {
                await this.updateMasterController(formValue);
            } else {
                await this.createMasterController(formValue);
            }
        } finally {
            this.isSaving.set(false);
        }
    }

    private async createMasterController(formValue: any): Promise<void> {
        const address: DeviceAddressDto = {
            firstAddressLine: formValue.addressLine1,
            secondAddressLine: formValue.addressLine2,
            locality: formValue.city,
            postCode: formValue.postalCode,
            region: formValue.region,
            countryCode: formValue.countryCode
        };

        const request: CreateMasterControllerRequest = {
            deviceName: formValue.deviceName,
            deviceDescription: formValue.deviceDescription,
            deviceHardwareID: formValue.deviceHardwareID,
            iotHubDeviceId: formValue.iotHubDeviceId,
            iotHubHostName: formValue.iotHubHostName,
            authenticationMethod: formValue.authenticationMethod,
            firmwareVersion: formValue.firmwareVersion,
            operatingSystem: formValue.operatingSystem,
            cpuModel: formValue.cpuModel,
            ramMb: formValue.ramMb ? parseInt(formValue.ramMb) : undefined,
            storageGb: formValue.storageGb ? parseInt(formValue.storageGb) : undefined,
            macAddress: formValue.macAddress,
            ipAddress: formValue.ipAddress,
            hostname: formValue.hostname,
            deviceAddress: address
        };

        try {
            const masterController = await firstValueFrom(this.masterService.masterControllersPost(request as any));

            // Create assignment if master controller created successfully
            if (masterController && masterController.id) {
                await this.createMasterDeviceAssignment(masterController.id, formValue);
            }
        } catch (error) {
            console.error('Error creating master controller:', error);
            this.snackBar.open('Failed to create master controller', 'Close', { duration: 5000 });
        }
    }

    private async createMasterDeviceAssignment(masterControllerId: string, formValue: any): Promise<void> {
        const parkingSpace = this.parkingSpace();
        if (!parkingSpace || !parkingSpace.parkingSpaceInfrastructureId) {
            this.snackBar.open('Parking space infrastructure not found', 'Close', { duration: 3000 });
            this.navigateBack();
            return;
        }

        const assignmentType = formValue.assignmentType as MasterAssignmentType;

        const request: CreateMasterDeviceAssignmentRequest = {
            masterDeviceId: masterControllerId,
            infrastructureId: parkingSpace.parkingSpaceInfrastructureId,
            assignmentType: assignmentType,
            // Device groups are attached by the create call itself — no per-group request
            deviceGroupIds: assignmentType === 'GroupSpecific' ? this.selectedDeviceGroupIds() : null
        };

        try {
            await firstValueFrom(
                this.masterDeviceAssignmentService.masterDeviceAssignmentsPost(request as any)
            );

            this.snackBar.open('Master controller and assignment created successfully', 'Close', { duration: 3000 });
            this.navigateBack();
        } catch (error) {
            console.error('Error creating master device assignment:', error);
            this.snackBar.open('Master controller created but failed to create assignment', 'Close', { duration: 5000 });
            this.navigateBack();
        }
    }

    private async updateMasterController(formValue: any): Promise<void> {
        if (!this.masterId) return;

        const address: DeviceAddressDto = {
            firstAddressLine: formValue.addressLine1,
            secondAddressLine: formValue.addressLine2,
            locality: formValue.city,
            postCode: formValue.postalCode,
            region: formValue.region,
            countryCode: formValue.countryCode
        };

        const request: UpdateMasterControllerRequest = {
            deviceName: formValue.deviceName,
            deviceDescription: formValue.deviceDescription,
            iotHubDeviceId: formValue.iotHubDeviceId,
            iotHubHostName: formValue.iotHubHostName,
            authenticationMethod: formValue.authenticationMethod,
            firmwareVersion: formValue.firmwareVersion,
            operatingSystem: formValue.operatingSystem,
            cpuModel: formValue.cpuModel,
            ramMb: formValue.ramMb ? parseInt(formValue.ramMb) : undefined,
            storageGb: formValue.storageGb ? parseInt(formValue.storageGb) : undefined,
            macAddress: formValue.macAddress,
            ipAddress: formValue.ipAddress,
            hostname: formValue.hostname,
            deviceAddress: address
        };

        try {
            await firstValueFrom(this.masterService.masterControllersIdPut(this.masterId, request as any));
            this.snackBar.open('Master controller updated successfully', 'Close', { duration: 3000 });
            this.navigateBack();
        } catch (error) {
            console.error('Error updating master controller:', error);
            this.snackBar.open('Failed to update master controller', 'Close', { duration: 5000 });
        }
    }

    private generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    generateHardwareID(): void {
        const newHardwareID = this.generateUUID();
        this.masterControllerForm.patchValue({
            deviceHardwareID: newHardwareID
        });
    }

    toggleInfrastructure(infrastructureId: string): void {
        const current = this.selectedInfrastructureIds();
        if (current.includes(infrastructureId)) {
            this.selectedInfrastructureIds.set(current.filter(id => id !== infrastructureId));
        } else {
            this.selectedInfrastructureIds.set([...current, infrastructureId]);
        }
    }

    toggleDeviceGroup(deviceGroupId: string): void {
        const current = this.selectedDeviceGroupIds();
        if (current.includes(deviceGroupId)) {
            this.selectedDeviceGroupIds.set(current.filter(id => id !== deviceGroupId));
        } else {
            this.selectedDeviceGroupIds.set([...current, deviceGroupId]);
        }
    }

    isInfrastructureSelected(infrastructureId: string): boolean {
        return this.selectedInfrastructureIds().includes(infrastructureId);
    }

    isDeviceGroupSelected(deviceGroupId: string): boolean {
        return this.selectedDeviceGroupIds().includes(deviceGroupId);
    }

    private async validateUniquIoTHubDeviceId(control: any): Promise<any> {
        const value = control.value;
        if (!value) {
            return null; // Don't validate empty values
        }

        // Skip validation in edit mode for the same device
        if (this.isEditMode()) {
            return null;
        }

        try {
            const result = await firstValueFrom(
                this.masterService.masterControllersGet(undefined, false, 0, 100)
            );

            if (result.items && result.items.some(m => m.iotHubDeviceId === value)) {
                return { notUnique: true };
            }
            return null;
        } catch (error) {
            console.error('Error validating IoT Hub Device ID:', error);
            return null; // Don't block submission on validation error
        }
    }

    navigateBack(): void {
        if (this.parkingSpaceId) {
            this.router.navigate(['/parking-space', this.parkingSpaceId]);
        } else {
            this.router.navigate(['/parking-space']);
        }
    }
}
